// Which plugins must be awake at boot. A manifest is lazy only if it opts in
// with activation: 'lazy' or 'idle' and nothing eager pulls it in; everything else (and
// every pack, which is just a list of members) starts loaded.

// Returns a Set with every id in `ids` plus, transitively, every dependency
// key that names a manifest in `manifests`. A pack (kind 'pack') is included
// but its dependencies are not followed — a pack lists its members, and a
// member must not be forced on just because some pack mentions it.
export function needsOf(ids, manifests) {
  const byId = new Map(manifests.map((m) => [m.id, m]))
  const result = new Set()
  const queue = [...ids]
  while (queue.length) {
    const cur = queue.shift()
    if (result.has(cur)) continue
    result.add(cur)
    const manifest = byId.get(cur)
    if (!manifest || manifest.kind === 'pack') continue
    for (const depId of Object.keys(manifest.dependencies ?? {})) {
      if (byId.has(depId) && !result.has(depId)) queue.push(depId)
    }
  }
  return result
}

// 'lazy' waits for first use, 'idle' for the first free moment after boot (kernel #startIdle); both sleep at boot.
const SLEEPS = new Set(['lazy', 'idle'])

// The ids that start at boot: every non-lazy manifest (packs never count as
// lazy) plus any woken id that names a manifest, closed over hard dependencies.
export function eagerIds(manifests, woken = new Set()) {
  const ids = new Set(manifests.map((m) => m.id))
  const seed = manifests
    .filter((m) => m.kind === 'pack' || !SLEEPS.has(m.activation))
    .map((m) => m.id)
  for (const id of woken) {
    if (ids.has(id)) seed.push(id)
  }
  return needsOf(seed, manifests)
}
